/**
 * Training-course fee helpers. The numbers live in the backend
 * `training_settings` collection now (editable via `/staff/training-fees`)
 * but we keep local defaults here so the app renders instantly on cold
 * load — the live settings replace them via `useTrainingSettings()`.
 */
export const VAT_RATE = 0.20;

export const BRAND_FEES_NET = {
  probound: 445,
  proepoxy: 745,
  prodecor: 525,
};

const BRAND_LABEL = {
  probound: 'ProBound',
  proepoxy: 'ProEpoxy',
  prodecor: 'ProDecor',
};

/** Return the "live" fees dict — settings override → defaults. */
export function resolveFees(settings) {
  const net = settings?.brand_fees_net || BRAND_FEES_NET;
  return { probound: net.probound ?? BRAND_FEES_NET.probound,
           proepoxy: net.proepoxy ?? BRAND_FEES_NET.proepoxy,
           prodecor: net.prodecor ?? BRAND_FEES_NET.prodecor };
}

export function resolveVat(settings) {
  const raw = settings?.vat_rate;
  return typeof raw === 'number' ? raw : VAT_RATE;
}

/**
 * Gross fee (VAT-inclusive) for a set of sections. Accepts an optional
 * `settings` object; falls back to the local defaults if omitted so
 * legacy call sites keep working while we roll live settings in.
 */
export function defaultCourseFeeForSections(sections, settings) {
  if (!Array.isArray(sections) || sections.length === 0) return null;
  const fees = resolveFees(settings);
  const vat = resolveVat(settings);
  let net = 0;
  for (const s of sections) {
    const price = fees[s];
    if (typeof price === 'number') net += price;
  }
  if (net === 0) return null;
  return Math.round(net * (1 + vat) * 100) / 100;
}

/** Human-readable breakdown, e.g. "Standard ProBound £445 + £89 VAT = £534". */
export function feeBreakdownLabel(sections, settings) {
  if (!Array.isArray(sections) || sections.length === 0) return '';
  const fees = resolveFees(settings);
  const vat = resolveVat(settings);
  const parts = sections
    .filter((s) => fees[s] != null)
    .map((s) => {
      const net = fees[s];
      const gross = Math.round(net * (1 + vat) * 100) / 100;
      const label = BRAND_LABEL[s] || s;
      return { section: s, label, net, gross };
    });
  if (parts.length === 0) return '';
  if (parts.length === 1) {
    const p = parts[0];
    const vatAmt = Math.round((p.gross - p.net) * 100) / 100;
    return `Standard ${p.label} £${p.net} + £${vatAmt} VAT = £${p.gross.toFixed(2)}`;
  }
  const total = parts.reduce((sum, p) => sum + p.gross, 0);
  const chunks = parts.map((p) => `${p.label} £${p.gross.toFixed(2)}`).join(' + ');
  return `Combined: ${chunks} = £${total.toFixed(2)}`;
}

/**
 * Compute the multi-course discount % for `count` courses. Returns
 * a decimal (0.05 / 0.10 / 0). Uses live settings when supplied.
 */
export function multiCourseDiscountPercent(count, settings) {
  if (!count || count < 2) return 0;
  const two = typeof settings?.discount_2_courses === 'number' ? settings.discount_2_courses : 0.05;
  const three = typeof settings?.discount_3_plus_courses === 'number' ? settings.discount_3_plus_courses : 0.10;
  return count === 2 ? two : three;
}

export const BRAND_LABELS = BRAND_LABEL;
